import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { persistAuthSession, prepareAuthForLogin, clearLoggedOutFlag } from "@/lib/authStorage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Shield, Loader2, CheckCircle, XCircle } from "lucide-react";

export default function AdminRegisterPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }
    api.get(`/auth/admin-invite/validate?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setValid(r.data.valid);
        setInviteEmail(r.data.email);
        setExpiresAt(r.data.expires_at);
      })
      .catch(() => setValid(false))
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (password.length < 8) {
      toast.error("Mot de passe trop court (8 caractères min.)");
      return;
    }
    setSubmitting(true);
    try {
      await prepareAuthForLogin();
      clearLoggedOutFlag();
      const { data } = await api.post("/auth/admin-invite/register", { token, name: name.trim(), password });
      await persistAuthSession(data, true);
      setUser(data);
      toast.success("Compte administrateur créé");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-800 bg-slate-900">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-heading text-sm font-bold text-white uppercase tracking-widest">COACH.</p>
              <p className="text-xs text-slate-400">Inscription administrateur</p>
            </div>
          </div>

          {!token || !valid ? (
            <div className="text-center py-6">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h1 className="text-lg font-semibold text-white mb-2">Invitation invalide</h1>
              <p className="text-sm text-slate-400 mb-6">
                Ce lien est expiré ou incorrect. Contactez un administrateur existant.
              </p>
              <Link to="/login" className="text-blue-400 text-sm hover:underline">Retour à la connexion</Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 bg-green-900/30 border border-green-800 rounded-xl px-4 py-3 mb-6">
                <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                <div>
                  <p className="text-sm text-green-300 font-medium">Invitation valide</p>
                  <p className="text-xs text-green-400/80">{inviteEmail}</p>
                  {expiresAt && (
                    <p className="text-[10px] text-green-400/60 mt-0.5">
                      Expire le {new Date(expiresAt).toLocaleString("fr-FR")}
                    </p>
                  )}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Nom complet</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 bg-slate-800 border-slate-700 text-white" required />
                </div>
                <div>
                  <Label className="text-slate-300">Mot de passe</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11 bg-slate-800 border-slate-700 text-white" minLength={8} required />
                </div>
                <div>
                  <Label className="text-slate-300">Confirmer le mot de passe</Label>
                  <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11 bg-slate-800 border-slate-700 text-white" required />
                </div>
                <Button type="submit" disabled={submitting} className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer mon compte admin"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}